import { useParams } from "react-router-dom";
import type { QaActionHistoryEntry } from "@gw/shared-types";
import { fmtET } from "@/lib/format";
import { useQaRawDataQuery } from "./useQaRawDataQuery";
import { RawJson } from "./components/RawJson";

/** Format a raw scalar for display; em-dash for null/undefined, JSON for nested objects. */
function show(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Format a raw timestamp value the same way the Detail screen does — `fmtET` converts to
 * America/Toronto (`YYYY-MM-DD HH:MM:SS ET`) so QA can compare without doing UTC→ET math. The
 * literal source value stays visible in the per-document Raw JSON block.
 */
function dt(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string" || value instanceof Date) return fmtET(value);
  return show(value); // unexpected non-date shape — fall back to raw
}

/** Safe nested lookup off the raw Cosmos document (e.g. get(req, "Analytics", "TemplateName")). */
function get(source: unknown, ...path: string[]): unknown {
  let current = source;
  for (const key of path) {
    if (current && typeof current === "object" && key in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return current;
}

function KeyValueTable({ rows }: { rows: Array<[string, unknown]> }) {
  return (
    <table className="stage-tbl qa-kv">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{show(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ActionHistoryTable({ entries }: { entries: QaActionHistoryEntry[] }) {
  if (entries.length === 0) {
    return <p className="qa-empty">No action history.</p>;
  }
  return (
    <table className="stage-tbl">
      <thead>
        <tr>
          <th className="numeric">#</th>
          <th>Action</th>
          <th>New status</th>
          <th>Actor</th>
          <th>Role</th>
          <th>Assigned</th>
          <th>Start</th>
          <th>End</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, i) => (
          <tr key={i}>
            <td className="numeric">{show(entry.ActionIndex)}</td>
            <td>{show(entry.ActionTaken)}</td>
            <td>{show(entry.NewStatus)}</td>
            <td>
              {show(entry.ActorName)}
              {entry.ActorEmail ? <span className="cell-secondary"> {entry.ActorEmail}</span> : null}
            </td>
            <td>{show(entry.ActorRole)}</td>
            <td>
              <span className="cell-secondary">{fmtET(entry.AssignedAt)}</span>
            </td>
            <td>
              <span className="cell-secondary">{fmtET(entry.StartDateTime)}</span>
            </td>
            <td>
              <span className="cell-secondary">{fmtET(entry.EndDateTime)}</span>
            </td>
            <td>{show(entry.ReasonMessage)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function QaScreen() {
  const { requestId } = useParams();
  const { data, isLoading, isError, error } = useQaRawDataQuery(requestId);

  if (isLoading) {
    return (
      <main className="screen qa" aria-busy="true">
        <p>Loading raw Cosmos data…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="screen qa">
        <div className="empty">
          <h4>Request not found in Cosmos</h4>
          <p>{(error as Error)?.message ?? "No Request document exists for this id."}</p>
        </div>
      </main>
    );
  }

  const request = data.request;
  const tasks = data.tasks ?? [];

  return (
    <main className="screen qa">
      <header className="qa-header">
        <h1>QA · Raw Cosmos data</h1>
        <p className="qa-id">{requestId}</p>
      </header>

      <section className="qa-section">
        <h2>Request</h2>
        <KeyValueTable
          rows={[
            ["RequestType", get(request, "RequestType")],
            ["Status", get(request, "Status")],
            ["CreatedDateTime", dt(get(request, "CreatedDateTime"))],
            ["Analytics.TemplateName", get(request, "Analytics", "TemplateName")],
            ["Analytics.ReleasedDateTime", dt(get(request, "Analytics", "ReleasedDateTime"))],
            ["Analytics.CompletedDateTime", dt(get(request, "Analytics", "CompletedDateTime"))],
          ]}
        />
        <RawJson value={request} />
      </section>

      <section className="qa-section">
        <h2>Request tasks ({tasks.length})</h2>
        {tasks.length === 0 ? (
          <p className="qa-empty">No task documents found for this request.</p>
        ) : (
          tasks.map((task, i) => {
            const history = get(task, "Analytics", "ActionHistory");
            const entries = Array.isArray(history) ? (history as QaActionHistoryEntry[]) : [];
            return (
              <article className="qa-task" key={(get(task, "id") as string) ?? i}>
                <KeyValueTable
                  rows={[
                    ["TaskType", get(task, "TaskType")],
                    ["TaskName", get(task, "TaskName")],
                    ["TaskStatus", get(task, "TaskStatus")],
                    ["CreatedDateTime", dt(get(task, "CreatedDateTime"))],
                  ]}
                />
                <h3>Analytics.ActionHistory</h3>
                <ActionHistoryTable entries={entries} />
                <RawJson value={task} />
              </article>
            );
          })
        )}
      </section>
    </main>
  );
}
